/**
 * Gesetzliche Feiertage für Kalender und Werktagsfristen (Mahnfristen, NR-07).
 * Region aus org_settings.holiday_region bzw. talents.holiday_region: 'DE', 'AT', 'CH' oder mit Unterregion
 * ('DE-BY', 'CH-ZH'). Deutschland: bundesweite Tage plus die Tage des Landes (alle 16 Länder). Österreich: die bundesweit
 * gesetzlichen Tage (Karfreitag ist seit 2019 kein allgemeiner Feiertag). Schweiz: je Kanton die Tage, die das kantonale
 * Recht dem Sonntag gleichstellt; Tage, die nur in einzelnen Gemeinden gelten, fehlen (sie sind keine Feiertage des
 * ganzen Kantons). 'CH' ohne Kanton: die in den meisten Kantonen geltenden Tage (genauer: Kanton wählen).
 * Sonntage, die ein Land ausdrücklich als Feiertag nennt (Oster- und Pfingstsonntag in Brandenburg und Hessen), stehen
 * im Kalender; für Werktagsfristen ändern sie nichts.
 */
import { addDays } from "./periods.js";
import { DomainError, type IsoDate, type Language } from "./types.js";

export interface Holiday {
  date: IsoDate;
  key: string;
}

/** Ostersonntag nach dem gregorianischen Kalender (Meeus/Jones/Butcher). */
export function easterSunday(year: number): IsoDate {
  if (!Number.isInteger(year) || year < 1900 || year > 2200) throw new DomainError("INVALID_YEAR", String(year));
  const a = year % 19, b = Math.floor(year / 100), c = year % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

const pad = (n: number) => String(n).padStart(2, "0");
const iso = (y: number, m: number, d: number): IsoDate => `${y}-${pad(m)}-${pad(d)}`;
/** Wochentag 0 = Sonntag … 6 = Samstag */
const dow = (date: IsoDate) => new Date(`${date}T12:00:00Z`).getUTCDay();
/** n-ter Wochentag eines Monats (n = 1 … 5) */
function nthWeekday(y: number, m: number, weekday: number, n: number): IsoDate {
  const first = iso(y, m, 1);
  const offset = (weekday - dow(first) + 7) % 7;
  return addDays(first, offset + (n - 1) * 7);
}

type Calc = (y: number, easter: IsoDate) => Holiday | null;
const fixed = (md: string, key: string): Calc => (y) => ({ date: `${y}-${md}`, key });
const easter = (days: number, key: string): Calc => (_y, e) => ({ date: addDays(e, days), key });

// Bausteine
const NEW_YEAR = fixed("01-01", "new_year");
const BERCHTOLD = fixed("01-02", "berchtold");
const EPIPHANY = fixed("01-06", "epiphany");
const WOMENS_DAY = fixed("03-08", "womens_day");
const ST_JOSEPH = fixed("03-19", "st_joseph");
const REPUBLIC_NE = fixed("03-01", "republic_day");
const GOOD_FRIDAY = easter(-2, "good_friday");
const EASTER_SUNDAY = easter(0, "easter_sunday");
const EASTER_MONDAY = easter(1, "easter_monday");
const LABOUR_DAY = fixed("05-01", "labour_day");
const ASCENSION = easter(39, "ascension");
const WHIT_SUNDAY = easter(49, "whit_sunday");
const WHIT_MONDAY = easter(50, "whit_monday");
const CORPUS_CHRISTI = easter(60, "corpus_christi");
const JURA_INDEPENDENCE = fixed("06-23", "jura_independence");
const PETER_PAUL = fixed("06-29", "st_peter_paul");
const NATIONAL_DAY_CH = fixed("08-01", "national_day");
const ASSUMPTION = fixed("08-15", "assumption");
const CHILDRENS_DAY = fixed("09-20", "childrens_day");
const BRUDER_KLAUS = fixed("09-25", "bruder_klaus");
const GERMAN_UNITY = fixed("10-03", "german_unity");
const NATIONAL_DAY_AT = fixed("10-26", "national_day");
const REFORMATION = fixed("10-31", "reformation_day");
const ALL_SAINTS = fixed("11-01", "all_saints");
const IMMACULATE = fixed("12-08", "immaculate_conception");
const CHRISTMAS = fixed("12-25", "christmas");
const ST_STEPHEN = fixed("12-26", "st_stephen");
const RESTORATION_GE = fixed("12-31", "restoration_day");
/** Buß- und Bettag: Mittwoch vor dem 23. November */
const REPENTANCE: Calc = (y) => ({ date: addDays(iso(y, 11, 22), -((dow(iso(y, 11, 22)) - 3 + 7) % 7)), key: "repentance_day" });
/** Näfelser Fahrt (Glarus): erster Donnerstag im April; fällt er in die Karwoche, eine Woche später */
const NAEFELS: Calc = (y, e) => {
  const d = nthWeekday(y, 4, 4, 1);
  return { date: d >= addDays(e, -7) && d < e ? addDays(d, 7) : d, key: "naefels" };
};
/** Genfer Bettag: Donnerstag nach dem ersten Sonntag im September */
const JEUNE_GENEVOIS: Calc = (y) => ({ date: addDays(nthWeekday(y, 9, 0, 1), 4), key: "jeune_genevois" });
/** Bettagsmontag (Waadt, Neuenburg): Montag nach dem dritten Sonntag im September */
const FAST_MONDAY: Calc = (y) => ({ date: addDays(nthWeekday(y, 9, 0, 3), 1), key: "fast_monday" });
/** Neuenburg: 2. Januar bzw. 26. Dezember nur, wenn Neujahr bzw. Weihnachten auf einen Sonntag fällt */
const NE_BERCHTOLD: Calc = (y) => (dow(iso(y, 1, 1)) === 0 ? { date: iso(y, 1, 2), key: "berchtold" } : null);
const NE_ST_STEPHEN: Calc = (y) => (dow(iso(y, 12, 25)) === 0 ? { date: iso(y, 12, 26), key: "st_stephen" } : null);

const DE_BASE: Calc[] = [NEW_YEAR, GOOD_FRIDAY, EASTER_MONDAY, LABOUR_DAY, ASCENSION, WHIT_MONDAY, GERMAN_UNITY, CHRISTMAS, ST_STEPHEN];
const AT_BASE: Calc[] = [NEW_YEAR, EPIPHANY, EASTER_MONDAY, LABOUR_DAY, ASCENSION, WHIT_MONDAY, CORPUS_CHRISTI, ASSUMPTION, NATIONAL_DAY_AT,
  ALL_SAINTS, IMMACULATE, CHRISTMAS, ST_STEPHEN];
/** Schweiz ohne Kanton: in den meisten Kantonen geltende Tage */
const CH_COMMON: Calc[] = [NEW_YEAR, GOOD_FRIDAY, EASTER_MONDAY, ASCENSION, WHIT_MONDAY, NATIONAL_DAY_CH, CHRISTMAS];

/** Zusätzliche Tage der 16 Länder */
const DE_STATES: Record<string, Calc[]> = {
  BW: [EPIPHANY, CORPUS_CHRISTI, ALL_SAINTS],
  BY: [EPIPHANY, CORPUS_CHRISTI, ALL_SAINTS],
  BE: [WOMENS_DAY],
  BB: [EASTER_SUNDAY, WHIT_SUNDAY, REFORMATION],
  HB: [REFORMATION],
  HH: [REFORMATION],
  HE: [EASTER_SUNDAY, WHIT_SUNDAY, CORPUS_CHRISTI],
  MV: [WOMENS_DAY, REFORMATION],
  NI: [REFORMATION],
  NW: [CORPUS_CHRISTI, ALL_SAINTS],
  RP: [CORPUS_CHRISTI, ALL_SAINTS],
  SL: [CORPUS_CHRISTI, ASSUMPTION, ALL_SAINTS],
  SN: [REFORMATION, REPENTANCE],
  ST: [EPIPHANY, REFORMATION],
  SH: [REFORMATION],
  TH: [CHILDRENS_DAY, REFORMATION],
};

/** Vollständige Liste je Kanton (Ruhetage nach kantonalem Recht, ohne Tage nur einzelner Gemeinden) */
const CH_CANTONS: Record<string, Calc[]> = {
  AG: [NEW_YEAR, GOOD_FRIDAY, EASTER_MONDAY, ASCENSION, WHIT_MONDAY, NATIONAL_DAY_CH, CHRISTMAS, ST_STEPHEN],
  AI: [NEW_YEAR, GOOD_FRIDAY, EASTER_MONDAY, ASCENSION, WHIT_MONDAY, CORPUS_CHRISTI, NATIONAL_DAY_CH, ASSUMPTION, ALL_SAINTS, IMMACULATE, CHRISTMAS, ST_STEPHEN],
  AR: [NEW_YEAR, GOOD_FRIDAY, EASTER_MONDAY, ASCENSION, WHIT_MONDAY, NATIONAL_DAY_CH, CHRISTMAS, ST_STEPHEN],
  BE: [NEW_YEAR, BERCHTOLD, GOOD_FRIDAY, EASTER_MONDAY, ASCENSION, WHIT_MONDAY, NATIONAL_DAY_CH, CHRISTMAS, ST_STEPHEN],
  BL: [NEW_YEAR, GOOD_FRIDAY, EASTER_MONDAY, LABOUR_DAY, ASCENSION, WHIT_MONDAY, NATIONAL_DAY_CH, CHRISTMAS, ST_STEPHEN],
  BS: [NEW_YEAR, GOOD_FRIDAY, EASTER_MONDAY, LABOUR_DAY, ASCENSION, WHIT_MONDAY, NATIONAL_DAY_CH, CHRISTMAS, ST_STEPHEN],
  FR: [NEW_YEAR, GOOD_FRIDAY, ASCENSION, CORPUS_CHRISTI, NATIONAL_DAY_CH, ASSUMPTION, ALL_SAINTS, IMMACULATE, CHRISTMAS],
  GE: [NEW_YEAR, GOOD_FRIDAY, EASTER_MONDAY, ASCENSION, WHIT_MONDAY, NATIONAL_DAY_CH, JEUNE_GENEVOIS, CHRISTMAS, RESTORATION_GE],
  GL: [NEW_YEAR, GOOD_FRIDAY, EASTER_MONDAY, NAEFELS, ASCENSION, WHIT_MONDAY, NATIONAL_DAY_CH, ALL_SAINTS, CHRISTMAS, ST_STEPHEN],
  GR: [NEW_YEAR, GOOD_FRIDAY, EASTER_MONDAY, ASCENSION, WHIT_MONDAY, NATIONAL_DAY_CH, CHRISTMAS, ST_STEPHEN],
  JU: [NEW_YEAR, BERCHTOLD, GOOD_FRIDAY, EASTER_MONDAY, LABOUR_DAY, ASCENSION, WHIT_MONDAY, CORPUS_CHRISTI, JURA_INDEPENDENCE, NATIONAL_DAY_CH,
    ASSUMPTION, ALL_SAINTS, CHRISTMAS],
  LU: [NEW_YEAR, GOOD_FRIDAY, EASTER_MONDAY, ASCENSION, WHIT_MONDAY, CORPUS_CHRISTI, NATIONAL_DAY_CH, ASSUMPTION, ALL_SAINTS, IMMACULATE, CHRISTMAS, ST_STEPHEN],
  NE: [NEW_YEAR, NE_BERCHTOLD, REPUBLIC_NE, GOOD_FRIDAY, EASTER_MONDAY, ASCENSION, WHIT_MONDAY, NATIONAL_DAY_CH, FAST_MONDAY, CHRISTMAS, NE_ST_STEPHEN],
  NW: [NEW_YEAR, ST_JOSEPH, GOOD_FRIDAY, EASTER_MONDAY, ASCENSION, WHIT_MONDAY, CORPUS_CHRISTI, NATIONAL_DAY_CH, ASSUMPTION, ALL_SAINTS, IMMACULATE,
    CHRISTMAS, ST_STEPHEN],
  OW: [NEW_YEAR, GOOD_FRIDAY, EASTER_MONDAY, ASCENSION, WHIT_MONDAY, CORPUS_CHRISTI, NATIONAL_DAY_CH, ASSUMPTION, BRUDER_KLAUS, ALL_SAINTS, IMMACULATE,
    CHRISTMAS, ST_STEPHEN],
  SG: [NEW_YEAR, GOOD_FRIDAY, EASTER_MONDAY, ASCENSION, WHIT_MONDAY, NATIONAL_DAY_CH, ALL_SAINTS, CHRISTMAS, ST_STEPHEN],
  SH: [NEW_YEAR, BERCHTOLD, GOOD_FRIDAY, EASTER_MONDAY, LABOUR_DAY, ASCENSION, WHIT_MONDAY, NATIONAL_DAY_CH, CHRISTMAS, ST_STEPHEN],
  SO: [NEW_YEAR, GOOD_FRIDAY, EASTER_MONDAY, ASCENSION, WHIT_MONDAY, CORPUS_CHRISTI, NATIONAL_DAY_CH, ASSUMPTION, ALL_SAINTS, CHRISTMAS, ST_STEPHEN],
  SZ: [NEW_YEAR, EPIPHANY, ST_JOSEPH, GOOD_FRIDAY, EASTER_MONDAY, ASCENSION, WHIT_MONDAY, CORPUS_CHRISTI, NATIONAL_DAY_CH, ASSUMPTION, ALL_SAINTS,
    IMMACULATE, CHRISTMAS, ST_STEPHEN],
  TG: [NEW_YEAR, BERCHTOLD, GOOD_FRIDAY, EASTER_MONDAY, LABOUR_DAY, ASCENSION, WHIT_MONDAY, NATIONAL_DAY_CH, CHRISTMAS, ST_STEPHEN],
  TI: [NEW_YEAR, EPIPHANY, ST_JOSEPH, EASTER_MONDAY, LABOUR_DAY, ASCENSION, WHIT_MONDAY, CORPUS_CHRISTI, PETER_PAUL, NATIONAL_DAY_CH, ASSUMPTION,
    ALL_SAINTS, IMMACULATE, CHRISTMAS, ST_STEPHEN],
  UR: [NEW_YEAR, EPIPHANY, ST_JOSEPH, GOOD_FRIDAY, EASTER_MONDAY, ASCENSION, WHIT_MONDAY, CORPUS_CHRISTI, NATIONAL_DAY_CH, ASSUMPTION, ALL_SAINTS,
    IMMACULATE, CHRISTMAS, ST_STEPHEN],
  VD: [NEW_YEAR, BERCHTOLD, GOOD_FRIDAY, EASTER_MONDAY, ASCENSION, WHIT_MONDAY, NATIONAL_DAY_CH, FAST_MONDAY, CHRISTMAS],
  VS: [NEW_YEAR, ST_JOSEPH, ASCENSION, CORPUS_CHRISTI, NATIONAL_DAY_CH, ASSUMPTION, ALL_SAINTS, IMMACULATE, CHRISTMAS],
  ZG: [NEW_YEAR, GOOD_FRIDAY, EASTER_MONDAY, ASCENSION, WHIT_MONDAY, CORPUS_CHRISTI, NATIONAL_DAY_CH, ASSUMPTION, ALL_SAINTS, IMMACULATE, CHRISTMAS, ST_STEPHEN],
  ZH: [NEW_YEAR, GOOD_FRIDAY, EASTER_MONDAY, LABOUR_DAY, ASCENSION, WHIT_MONDAY, NATIONAL_DAY_CH, CHRISTMAS, ST_STEPHEN],
};

export function publicHolidays(region: string, year: number): Holiday[] {
  const [country, sub] = region.toUpperCase().split("-");
  const e = easterSunday(year);
  let calcs: Calc[];
  switch (country) {
    case "DE":
      calcs = [...DE_BASE, ...(sub ? DE_STATES[sub] ?? [] : [])];
      break;
    case "AT":
      calcs = AT_BASE;
      break;
    case "CH":
      calcs = sub && CH_CANTONS[sub] ? CH_CANTONS[sub]! : CH_COMMON;
      break;
    default:
      throw new DomainError("UNKNOWN_HOLIDAY_REGION", region);
  }
  const byDate = new Map<IsoDate, Holiday>();
  for (const c of calcs) {
    const h = c(year, e);
    if (h && !byDate.has(h.date)) byDate.set(h.date, h);
  }
  return [...byDate.values()].sort((a, b) => (a.date < b.date ? -1 : 1));
}

/** Nur die Daten, für Werktagsrechnungen (addWorkingDays, holidays-Parameter). */
export function holidayDates(region: string, years: readonly number[]): IsoDate[] {
  return years.flatMap((y) => publicHolidays(region, y).map((h) => h.date));
}

/** Wie holidayDates, aber ohne Region oder bei unbekannter Region leer (Rechnungsdatum an Werktagen, SAAS-5). */
export function holidayDatesOrNone(region: string | null | undefined, years: readonly number[]): IsoDate[] {
  if (!region?.trim()) return [];
  try {
    return holidayDates(region.trim(), years);
  } catch {
    return [];
  }
}

// ── Regionen und Namen ─────────────────────────────────────────────────────────

export interface HolidayRegion {
  key: string;
  label: string;
  label_en: string;
}

const CANTON_NAMES: [string, string, string][] = [
  ["AG", "Aargau", "Aargau"], ["AI", "Appenzell Innerrhoden", "Appenzell Innerrhoden"], ["AR", "Appenzell Ausserrhoden", "Appenzell Ausserrhoden"],
  ["BE", "Bern", "Bern"], ["BL", "Basel-Landschaft", "Basel-Landschaft"], ["BS", "Basel-Stadt", "Basel-Stadt"], ["FR", "Freiburg", "Fribourg"],
  ["GE", "Genf", "Geneva"], ["GL", "Glarus", "Glarus"], ["GR", "Graubünden", "Grisons"], ["JU", "Jura", "Jura"], ["LU", "Luzern", "Lucerne"],
  ["NE", "Neuenburg", "Neuchâtel"], ["NW", "Nidwalden", "Nidwalden"], ["OW", "Obwalden", "Obwalden"], ["SG", "St. Gallen", "St. Gallen"],
  ["SH", "Schaffhausen", "Schaffhausen"], ["SO", "Solothurn", "Solothurn"], ["SZ", "Schwyz", "Schwyz"], ["TG", "Thurgau", "Thurgau"],
  ["TI", "Tessin", "Ticino"], ["UR", "Uri", "Uri"], ["VD", "Waadt", "Vaud"], ["VS", "Wallis", "Valais"], ["ZG", "Zug", "Zug"], ["ZH", "Zürich", "Zurich"],
];

/** Wählbare Feiertagsregionen (Einstellungen der Agentur, eigene Region des Creators). Jede hat eine vollständige Liste. */
export const HOLIDAY_REGIONS: readonly HolidayRegion[] = [
  { key: "DE", label: "Deutschland (bundesweite Feiertage)", label_en: "Germany (nationwide holidays)" },
  { key: "DE-BW", label: "Baden-Württemberg", label_en: "Baden-Württemberg" }, { key: "DE-BY", label: "Bayern", label_en: "Bavaria" },
  { key: "DE-BE", label: "Berlin", label_en: "Berlin" }, { key: "DE-BB", label: "Brandenburg", label_en: "Brandenburg" },
  { key: "DE-HB", label: "Bremen", label_en: "Bremen" }, { key: "DE-HH", label: "Hamburg", label_en: "Hamburg" },
  { key: "DE-HE", label: "Hessen", label_en: "Hesse" }, { key: "DE-MV", label: "Mecklenburg-Vorpommern", label_en: "Mecklenburg-Western Pomerania" },
  { key: "DE-NI", label: "Niedersachsen", label_en: "Lower Saxony" }, { key: "DE-NW", label: "Nordrhein-Westfalen", label_en: "North Rhine-Westphalia" },
  { key: "DE-RP", label: "Rheinland-Pfalz", label_en: "Rhineland-Palatinate" }, { key: "DE-SL", label: "Saarland", label_en: "Saarland" },
  { key: "DE-SN", label: "Sachsen", label_en: "Saxony" }, { key: "DE-ST", label: "Sachsen-Anhalt", label_en: "Saxony-Anhalt" },
  { key: "DE-SH", label: "Schleswig-Holstein", label_en: "Schleswig-Holstein" }, { key: "DE-TH", label: "Thüringen", label_en: "Thuringia" },
  { key: "AT", label: "Österreich", label_en: "Austria" },
  { key: "CH", label: "Schweiz (verbreitete Feiertage; genauer: Kanton wählen)", label_en: "Switzerland (common holidays; choose a canton for more)" },
  ...CANTON_NAMES.map(([k, de, en]) => ({ key: `CH-${k}`, label: `Kanton ${de}`, label_en: `Canton of ${en}` })),
];

const NAMES: Record<string, [string, string]> = {
  new_year: ["Neujahr", "New Year's Day"], berchtold: ["Berchtoldstag", "St Berchtold's Day"], epiphany: ["Heilige Drei Könige", "Epiphany"],
  womens_day: ["Internationaler Frauentag", "International Women's Day"], st_joseph: ["Josefstag", "St Joseph's Day"],
  republic_day: ["Jahrestag der Ausrufung der Republik", "Republic Day"], good_friday: ["Karfreitag", "Good Friday"],
  easter_sunday: ["Ostersonntag", "Easter Sunday"], easter_monday: ["Ostermontag", "Easter Monday"], naefels: ["Näfelser Fahrt", "Näfels Pilgrimage"],
  labour_day: ["Tag der Arbeit", "Labour Day"], ascension: ["Christi Himmelfahrt", "Ascension Day"], whit_sunday: ["Pfingstsonntag", "Whit Sunday"],
  whit_monday: ["Pfingstmontag", "Whit Monday"], corpus_christi: ["Fronleichnam", "Corpus Christi"],
  jura_independence: ["Fest der Unabhängigkeit", "Jura Independence Day"], st_peter_paul: ["Peter und Paul", "St Peter and St Paul"],
  national_day: ["Nationalfeiertag", "National Day"], assumption: ["Mariä Himmelfahrt", "Assumption Day"],
  childrens_day: ["Weltkindertag", "World Children's Day"], bruder_klaus: ["Bruder-Klausen-Fest", "St Nicholas of Flüe"],
  jeune_genevois: ["Genfer Bettag", "Geneva Fast"], fast_monday: ["Bettagsmontag", "Federal Fast Monday"],
  german_unity: ["Tag der Deutschen Einheit", "German Unity Day"], reformation_day: ["Reformationstag", "Reformation Day"],
  all_saints: ["Allerheiligen", "All Saints' Day"], repentance_day: ["Buß- und Bettag", "Day of Repentance and Prayer"],
  immaculate_conception: ["Mariä Empfängnis", "Immaculate Conception"], christmas: ["1. Weihnachtstag", "Christmas Day"],
  st_stephen: ["2. Weihnachtstag", "Boxing Day"], restoration_day: ["Wiederherstellung der Republik", "Restoration of the Republic"],
};
const COUNTRY_NAMES: Record<string, Record<string, [string, string]>> = {
  AT: { labour_day: ["Staatsfeiertag", "Labour Day"], christmas: ["Christtag", "Christmas Day"], st_stephen: ["Stefanitag", "St Stephen's Day"] },
  CH: {
    national_day: ["Bundesfeiertag", "Swiss National Day"], ascension: ["Auffahrt", "Ascension Day"], christmas: ["Weihnachten", "Christmas Day"],
    st_stephen: ["Stephanstag", "St Stephen's Day"],
  },
};

/** Name eines Feiertags in der Sprache der Person, mit den Bezeichnungen des Landes (Staatsfeiertag, Auffahrt …). */
export function holidayName(key: string, region: string, language: Language = "de"): string {
  const country = region.toUpperCase().split("-")[0] ?? "";
  const pair = COUNTRY_NAMES[country]?.[key] ?? NAMES[key];
  return pair ? pair[language === "en" ? 1 : 0] : key;
}
